import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useDrag, useDragLayer } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import { MosaicDragType } from 'react-mosaic-component'
import type { MosaicBranch } from 'react-mosaic-component'
import { ChevronDown, ChevronRight, Lock, Plus, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { trackEvent, EVENTS } from '@/services/analytics'
import type { ComponentType } from '@/types/schemas/workspace'
import { COMPONENT_META } from './add-panel-menu-constants'
import { groupByCategory, useCollapsedCategories } from './add-panel-menu-helpers'
import { PANEL_CARD_ART } from './panel-card-art'
import { searchPanelTypes } from './panel-search'
import {
  PANEL_HUB_WIDTH_PX,
  WORKSPACE_MOSAIC_ID,
  finishHubDrag,
  isPanelHubDragItem,
  type PanelHubDragItem,
  type PanelHubDropResult,
} from './panel-hub-constants'
import type { DropPosition } from './resolve-drop'
import {
  requestPanelAdd,
  usePanelAvailability,
  type PanelAvailability,
} from './use-panel-availability'

/**
 * The visual "Add Panel" rail: a two-column grid of cards, one per panel
 * type, each with a small infographic. A card adds with its plus button (the
 * existing placement gesture then asks where) or is dragged straight onto the
 * mosaic, where react-mosaic's own edge drop targets take it.
 *
 * It is an overlay over the mosaic (scrim plus a sheet from the right), not a
 * sibling in the flex row: opening it must not reflow the layout it is adding
 * to (Chris, 2026-09-15). While a card is in flight the scrim goes away and
 * the sheet slides off-screen so the whole mosaic is a drop surface. The
 * sheet hides by transform + visibility, never by unmounting: Chrome never
 * fires `dragend` when the drag source leaves the DOM mid-drag, which would
 * lose the drop, so the source card has to stay mounted until `end`.
 */
export interface PanelHubProps {
  availableTypes: ComponentType[]
  panelCounts: Partial<Record<ComponentType, number>>
  /** Plus button / card click. Already gated: only called for an allowed add. */
  onAdd: (componentType: ComponentType) => void
  /**
   * A card was dropped on a mosaic edge. Returns whether a panel was inserted
   * (a per-type ceiling can still refuse at drop time).
   */
  onDrop: (componentType: ComponentType, path: MosaicBranch[], position: DropPosition) => boolean
  onClose: () => void
  /** No drag surface right now (placement or move pending, grid mode, locked). */
  dragDisabled?: boolean
  /** Esc belongs to the pending placement/move gesture while one is active. */
  suspendEsc?: boolean
  /**
   * Draw category headers with fold chevrons. Off for the dashboard (Chris,
   * 2026-09-15): thirteen pictures scan faster than two labelled groups, and
   * `availableTypes` order already puts the core panels first. The History
   * hub, with three real groups over eighteen types, is the case for it.
   */
  grouped?: boolean
}

export function PanelHub({
  availableTypes,
  panelCounts,
  onAdd,
  onDrop,
  onClose,
  dragDisabled = false,
  suspendEsc = false,
  grouped = false,
}: PanelHubProps) {
  const { resolve } = usePanelAvailability(panelCounts)
  const { collapsed, toggle } = useCollapsedCategories()
  const [dragging, setDragging] = useState(false)
  // Search narrows the grid to matching cards, best first (null = no query,
  // full grid). A search result is one ranked list, so it ignores the groups.
  const [query, setQuery] = useState('')
  const matches = useMemo(() => searchPanelTypes(query, availableTypes), [availableTypes, query])

  const cardFor = (type: ComponentType) => (
    <PanelCard
      key={type}
      availability={resolve(type)}
      onAdd={onAdd}
      onDrop={onDrop}
      dragDisabled={dragDisabled}
      onDragState={handleDragState}
    />
  )

  const handleDragState = useCallback(
    (active: boolean, landed = false) => {
      setDragging(active)
      // A drop that inserted a panel is the end of the errand; a drag that
      // came back empty (dropped outside, refused) brings the rail back.
      if (!active && landed) onClose()
    },
    [onClose],
  )

  // Esc clears a search before it closes the hub.
  useEffect(() => {
    if (suspendEsc) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (query) setQuery('')
      else onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, suspendEsc, query])

  // Enter adds the top match, through the same gate as a click on its card.
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return
    const top = matches?.[0]
    if (!top) return
    e.preventDefault()
    const availability = resolve(top)
    if (requestPanelAdd(availability)) onAdd(top)
  }

  return (
    <>
      {/* Scrim: a light darkening over the mosaic, click to dismiss. Display
          none while a card is in flight so the mosaic's drop targets receive
          the dragover/drop events. Kept mounted so its fade-in plays once. */}
      <div
        data-testid="panel-hub-scrim"
        aria-hidden="true"
        // Desktop app: the scrim and the hub cover the page's top row, which would
        // otherwise drag the window instead of taking their clicks.
        data-window-no-drag
        onClick={onClose}
        className="absolute inset-0 z-60 bg-black/40 animate-in fade-in duration-200"
        style={{ display: dragging ? 'none' : undefined }}
      />
      {/* The entrance animation classes never change, so a dragging state
          flip cannot replay them; the in-flight hide is inline style only. */}
      <aside
        data-testid="panel-hub"
        data-dragging={dragging || undefined}
        data-window-no-drag
        aria-label="Add Panel"
        className="absolute inset-y-0 right-0 z-60 flex flex-col border-l border-border bg-background shadow-[-8px_0_32px_rgba(0,0,0,0.35)] animate-in slide-in-from-right duration-200"
        style={{
          width: PANEL_HUB_WIDTH_PX,
          transform: dragging ? 'translateX(100%)' : undefined,
          visibility: dragging ? 'hidden' : undefined,
          transition: 'transform 200ms ease-out, visibility 200ms',
        }}
      >
        <div className="flex h-10 shrink-0 items-center gap-2 border-b border-border px-3">
          <span className="text-sm font-semibold text-foreground">Add Panel</span>
          <span className="text-[11px] text-muted-foreground">click + or drag onto the layout</span>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-hover hover:text-foreground"
            aria-label="Close panel hub"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="shrink-0 px-3 pt-3">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              aria-label="Search panels"
              placeholder="Search panels..."
              // No autoFocus: focusing during the slide-in makes the browser
              // scroll the input into view and breaks the entrance animation.
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-full rounded-md border border-border bg-background py-1 pl-7 pr-2 text-[13px] outline-none placeholder:text-muted-foreground focus:border-primary/50"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
          {matches != null && matches.length > 0 && (
            <div className="grid grid-cols-2 gap-3">{matches.map(cardFor)}</div>
          )}
          {matches != null && matches.length === 0 && (
            <p className="px-0.5 py-6 text-center text-[12px] text-muted-foreground">
              No panels match &ldquo;{query.trim()}&rdquo;
            </p>
          )}
          {matches == null && !grouped && (
            <div className="grid grid-cols-2 gap-3">{availableTypes.map(cardFor)}</div>
          )}
          {matches == null &&
            grouped &&
            groupByCategory(availableTypes).map(({ category, types }) => {
              const isCollapsed = category != null && collapsed.has(category)
              return (
                <section key={category ?? 'uncategorized'} className="mb-2">
                  {category && (
                    <button
                      type="button"
                      onClick={() => toggle(category)}
                      aria-expanded={!isCollapsed}
                      className="flex w-full items-center gap-1 px-0.5 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {isCollapsed ? (
                        <ChevronRight className="h-3 w-3" />
                      ) : (
                        <ChevronDown className="h-3 w-3" />
                      )}
                      {category}
                    </button>
                  )}
                  {!isCollapsed && (
                    <div className="grid grid-cols-2 gap-3">{types.map(cardFor)}</div>
                  )}
                </section>
              )
            })}
        </div>
      </aside>
      <PanelDragLayer />
    </>
  )
}

// ─── Card ───

interface PanelCardProps {
  availability: PanelAvailability
  onAdd: (componentType: ComponentType) => void
  onDrop: PanelHubProps['onDrop']
  dragDisabled: boolean
  onDragState: (active: boolean, landed?: boolean) => void
}

function PanelCard({ availability, onAdd, onDrop, dragDisabled, onDragState }: PanelCardProps) {
  const { type, isLocked, atLimit, disabled, badgeText } = availability
  const meta = COMPONENT_META[type]
  const Icon = meta.icon
  const Art = PANEL_CARD_ART[type]
  // A locked or plan-capped card still CLICKS (the refusal sells the upgrade)
  // but never drags: there is nothing to place.
  const canDrag = !dragDisabled && !disabled && !isLocked && !atLimit
  const hideTimerRef = useRef<number | undefined>(undefined)

  const [{ isDragging }, dragRef, previewRef] = useDrag<
    PanelHubDragItem,
    PanelHubDropResult,
    { isDragging: boolean }
  >(
    () => ({
      type: MosaicDragType.WINDOW,
      canDrag,
      item: () => {
        // Chrome snapshots the drag image from the source element one frame
        // AFTER dragstart returns. Hiding the sheet synchronously here leaves
        // nothing to snapshot and Chrome cancels the drag outright (dragend
        // fires at once). Defer the hide by a tick, exactly as react-mosaic
        // defers its own window hide (its `hideTimer`).
        hideTimerRef.current = window.setTimeout(() => onDragState(true), 0)
        return { mosaicId: WORKSPACE_MOSAIC_ID, hideTimer: 0, componentType: type }
      },
      end: (item, monitor) => {
        window.clearTimeout(hideTimerRef.current)
        const landed = finishHubDrag(item, monitor.getDropResult(), onDrop)
        if (landed) trackEvent(EVENTS.PANEL_ADDED, { component_type: item.componentType })
        onDragState(false, landed)
      },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [type, canDrag, onDrop, onDragState],
  )

  // The custom drag layer draws the card under the cursor; blank out the
  // browser's own washed-out snapshot so the two do not stack.
  useEffect(() => {
    previewRef(getEmptyImage(), { captureDraggingState: true })
  }, [previewRef])

  const handleAdd = () => {
    if (requestPanelAdd(availability)) onAdd(type)
  }

  return (
    // The plus is the one real control (keyboard-reachable, disabled when the
    // ceiling bites); the card body is a click convenience, so it carries no
    // ARIA role of its own and does not nest a button inside a button.
    <div
      ref={(node) => {
        dragRef(node)
      }}
      data-testid={`panel-card-${type}`}
      data-can-drag={canDrag || undefined}
      data-disabled={disabled || undefined}
      onClick={disabled ? undefined : handleAdd}
      className={cn(
        // Soft edges (Chris, 2026-09-15): the tile colour does the separating,
        // the hairline only hints at it.
        'group relative flex select-none flex-col rounded-lg border border-border/40 bg-card text-left transition-colors',
        disabled
          ? 'cursor-not-allowed opacity-50'
          : canDrag
            ? 'cursor-grab hover:border-primary/50 active:cursor-grabbing'
            : 'cursor-pointer hover:border-primary/50',
        isDragging && 'opacity-40',
      )}
    >
      {/* Title first, flush with the picture's left edge; the picture below. */}
      <div className="flex items-center gap-1.5 pb-1 pl-2.5 pr-2.5 pt-2">
        <span className="truncate text-sm font-medium text-foreground">{meta.label}</span>
        {isLocked && !atLimit && (
          <Lock className="ml-auto h-3 w-3 shrink-0 text-muted-foreground" />
        )}
      </div>
      <div className={cn('aspect-[16/10] w-full px-2 pb-2', (isLocked || atLimit) && 'opacity-60')}>
        {Art ? (
          <Art />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <Icon className="h-8 w-8" strokeWidth={1.5} />
          </div>
        )}
      </div>
      {/* A reached limit is stated under the picture, not beside the title. */}
      {badgeText && (
        <div className="-mt-2 px-2.5 pb-1.5 text-[10px] leading-none text-muted-foreground">
          {badgeText}
        </div>
      )}
      {/* The add control is a round plus floating off the card's left edge,
          centred vertically, shown on hover or focus so the grid reads as
          pictures, not a wall of buttons. The card body still adds on click. */}
      <button
        type="button"
        disabled={disabled}
        aria-label={`Add ${meta.label} panel`}
        onClick={(e) => {
          e.stopPropagation()
          handleAdd()
        }}
        className={cn(
          'absolute -left-2.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-opacity',
          'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
          disabled && 'hidden',
        )}
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
      </button>
    </div>
  )
}

// ─── Drag layer ───

/**
 * Draws the in-flight card at the cursor. Portaled to <body> because the rail
 * that hosts the source card clips to zero width during the drag.
 */
function PanelDragLayer() {
  const { item, itemType, offset, isDragging } = useDragLayer((monitor) => ({
    item: monitor.getItem() as unknown,
    itemType: monitor.getItemType(),
    offset: monitor.getClientOffset(),
    isDragging: monitor.isDragging(),
  }))
  // Existing tiles drag with the same react-dnd type but no componentType;
  // those keep react-mosaic's own preview.
  if (!isDragging || itemType !== MosaicDragType.WINDOW || !isPanelHubDragItem(item) || !offset) {
    return null
  }
  const meta = COMPONENT_META[item.componentType]
  const Art = PANEL_CARD_ART[item.componentType]
  const Icon = meta.icon
  return createPortal(
    <div
      className="pointer-events-none fixed left-0 top-0 z-[1000]"
      style={{ transform: `translate(${offset.x - 70}px, ${offset.y - 44}px)` }}
    >
      <div className="w-[140px] overflow-hidden rounded-lg border border-primary/60 bg-card shadow-xl">
        <div className="aspect-[16/10] w-full p-1.5">
          {Art ? (
            <Art />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <Icon className="h-6 w-6" strokeWidth={1.5} />
            </div>
          )}
        </div>
        <div className="border-t border-border px-2 py-1 text-[11px] font-medium text-foreground">
          {meta.label}
        </div>
      </div>
    </div>,
    document.body,
  )
}
